import { useState } from "react";
import { CategoryIcon, resolveCategoryForIcon } from "../../../lib/categoryIcons";
import { getArticleStatus, STATUS_CLASSES } from "../../../lib/stockDisplay";
import { StockArticleActions } from "../../../components/StockArticleActions";
import { ChevronRight, ChevronDown, CornerDownRight } from "lucide-react";

// Modèle 4 — Groupé par catégorie : sections dépliables par catégorie
// principale, avec les sous-catégories bien visibles dedans.
export function GroupedCategoryView({ articles, categories, isTestMode, user, canEditAnyStockField, canTransfer, onEdit, onTransfer, onDelete, uploadsBase }) {
  const [ouvertes, setOuvertes] = useState({});
  const estOuverte = (nom) => ouvertes[nom] !== false; // ouvert par défaut

  // Regroupement : catégorie principale -> { direct: [articles sans sous-catégorie
  // ou dont la catégorie est elle-même de premier niveau], subs: { sousNom: [...] } }
  const categoriesPrincipales = categories.filter((c) => !c.parent_id);
  const groupes = {};
  categoriesPrincipales.forEach((p) => { groupes[p.nom] = { direct: [], subs: {} }; });
  groupes["__sans_categorie__"] = { direct: [], subs: {} };

  articles.forEach((a) => {
    const cat = categories.find((c) => c.nom === a.categorie);
    if (!cat) {
      (groupes["__sans_categorie__"] ||= { direct: [], subs: {} }).direct.push(a);
      return;
    }
    if (cat.parent_id) {
      const parent = categories.find((c) => c.id === cat.parent_id);
      const parentNom = parent ? parent.nom : "__sans_categorie__";
      groupes[parentNom] ||= { direct: [], subs: {} };
      (groupes[parentNom].subs[cat.nom] ||= []).push(a);
    } else {
      (groupes[cat.nom] ||= { direct: [], subs: {} }).direct.push(a);
    }
  });

  const ArticleRow = ({ a, indent }) => {
    const s = getArticleStatus(a, isTestMode);
    const cls = STATUS_CLASSES[s.key];
    return (
      <div
        className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-secondary/60 cursor-pointer group ${indent ? "ml-5" : ""}`}
        onClick={() => canEditAnyStockField && onEdit(a)} data-testid={`stock-grouped-row-${a.id}`}
      >
        <div className="w-7 h-7 rounded-md bg-secondary flex items-center justify-center overflow-hidden shrink-0">
          {a.photo_url ? (
            <img src={`${uploadsBase}${a.photo_url}`} alt={a.nom} className="w-full h-full object-cover" />
          ) : (
            <CategoryIcon category={resolveCategoryForIcon(categories, a.categorie)} uploadsBase={uploadsBase} size={13} className="text-muted-foreground" />
          )}
        </div>
        <span className="text-[13px] flex-1 truncate">{a.nom}</span>
        <span className={`text-[10px] font-mono shrink-0 ${cls.text}`}>Qté {a.quantite}</span>
        <span className="text-xs font-mono font-semibold shrink-0 w-16 text-right">{Number(a.prix_ttc ?? a.prix ?? 0).toFixed(2)} €</span>
        <StockArticleActions
          article={a} user={user} canEditAnyStockField={canEditAnyStockField} canTransfer={canTransfer}
          onEdit={onEdit} onTransfer={onTransfer} onDelete={onDelete}
          className="flex gap-1 opacity-0 group-hover:opacity-100 shrink-0"
        />
      </div>
    );
  };

  const entries = Object.entries(groupes).filter(([, g]) => g.direct.length > 0 || Object.values(g.subs).some((arr) => arr.length > 0));

  return (
    <div className="max-w-3xl mx-auto space-y-2.5" data-testid="stock-view-groupe">
      {entries.map(([nom, g]) => {
        const total = g.direct.length + Object.values(g.subs).reduce((s, arr) => s + arr.length, 0);
        const ouverte = estOuverte(nom);
        const categorie = categories.find((c) => c.nom === nom);
        return (
          <div key={nom} className="rounded-xl border border-border bg-card overflow-hidden">
            <button
              className="w-full flex items-center gap-2.5 px-4 py-3 bg-secondary/60 text-left"
              onClick={() => setOuvertes((o) => ({ ...o, [nom]: !estOuverte(nom) }))}
              data-testid={`stock-grouped-header-${nom}`}
            >
              {ouverte ? <ChevronDown size={14} className="text-muted-foreground" /> : <ChevronRight size={14} className="text-muted-foreground" />}
              {categorie && <CategoryIcon category={categorie} uploadsBase={uploadsBase} size={15} className="text-muted-foreground" />}
              <span className="text-sm font-semibold">{nom === "__sans_categorie__" ? "Sans catégorie" : nom}</span>
              <span className="ml-auto text-xs text-muted-foreground font-mono">{total} article(s)</span>
            </button>
            {ouverte && (
              <div className="p-2 space-y-0.5">
                {g.direct.map((a) => <ArticleRow key={a.id} a={a} />)}
                {Object.entries(g.subs).map(([subNom, arr]) => arr.length > 0 && (
                  <div key={subNom}>
                    <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground px-2 pt-2 pb-1">
                      <CornerDownRight size={11} /> {subNom}
                    </p>
                    {arr.map((a) => <ArticleRow key={a.id} a={a} indent />)}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
      {entries.length === 0 && (
        <p className="p-6 text-center text-sm text-muted-foreground">Aucun article.</p>
      )}
    </div>
  );
}
